import React, { useState, useEffect, useCallback } from 'react';
import { Phone, Mail, MapPin, Search, RefreshCw, Trash2, Eye, Edit2, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import api from '../api';
import { formatDate } from '../constants';

const statusLabels = {
  nouveau: { label: 'Nouveau', color: 'bg-blue-100 text-blue-800' },
  en_cours: { label: 'En cours', color: 'bg-yellow-100 text-yellow-800' },
  traite: { label: 'Traité', color: 'bg-green-100 text-green-800' },
  archive: { label: 'Archivé', color: 'bg-gray-100 text-gray-800' },
};
const serviceLabels = { vitrerie: 'Vitrerie', serrurerie: 'Serrurerie', menuiserie: 'Menuiserie', securisation: 'Sécurisation', maintenance: 'Maintenance', autre: 'Autre' };

const AdminContactRequests = () => {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editNotes, setEditNotes] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [filters, setFilters] = useState({ status: 'all', search: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      const [r, s] = await Promise.all([
        api.get(`/contact?${params.toString()}`),
        api.get('/contact/stats'),
      ]);
      setRequests(r.data);
      setStats(s.data);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateRequest = async () => {
    try {
      await api.patch(`/contact/${selected.id}`, { status: editStatus || undefined, notes: editNotes });
      toast.success('Demande mise à jour');
      setShowEdit(false);
      fetchData();
    } catch (e) { toast.error('Erreur de mise à jour'); }
  };

  const deleteRequest = async (id) => {
    if (!window.confirm('Supprimer cette demande ?')) return;
    try { await api.delete(`/contact/${id}`); toast.success('Supprimée'); fetchData(); }
    catch (e) { toast.error('Erreur de suppression'); }
  };

  return (
    <div data-testid="admin-contact-requests">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Demandes site web</h1>
          <p className="text-neutral-500">Formulaire de contact public</p>
        </div>
        <Button onClick={fetchData} variant="outline" data-testid="refresh-contact-button">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Actualiser
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{stats.total}</p><p className="text-xs text-neutral-500">Total</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-blue-600">{stats.nouveau}</p><p className="text-xs text-neutral-500">Nouveaux</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-red-600">{stats.urgences}</p><p className="text-xs text-neutral-500">Urgences</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{stats.today}</p><p className="text-xs text-neutral-500">Aujourd'hui</p></CardContent></Card>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input placeholder="Rechercher..." value={filters.search} onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))} className="pl-10 bg-white" />
        </div>
        <Select value={filters.status} onValueChange={(v) => setFilters((p) => ({ ...p, status: v }))}>
          <SelectTrigger className="w-full sm:w-44 bg-white"><SelectValue placeholder="Statut" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="nouveau">Nouveau</SelectItem>
            <SelectItem value="en_cours">En cours</SelectItem>
            <SelectItem value="traite">Traité</SelectItem>
            <SelectItem value="archive">Archivé</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-red-700" /></div>
      ) : requests.length === 0 ? (
        <div className="bg-white rounded-xl border border-neutral-200 p-12 text-center text-neutral-400">Aucune demande.</div>
      ) : (
        <div className="space-y-3">
          {requests.map((r) => {
            const si = statusLabels[r.status] || statusLabels.nouveau;
            return (
              <Card key={r.id} className={r.isUrgent ? 'border-l-4 border-l-red-500' : ''}>
                <CardContent className="p-4 flex flex-col lg:flex-row lg:items-center gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {r.isUrgent && <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded font-bold">URGENT</span>}
                      <span className={`text-xs px-2 py-0.5 rounded ${si.color}`}>{si.label}</span>
                      <span className="text-xs bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded">{serviceLabels[r.serviceType] || r.serviceType}</span>
                    </div>
                    <h3 className="font-bold text-neutral-900">{r.name}</h3>
                    <div className="flex flex-wrap gap-4 mt-1 text-sm text-neutral-600">
                      <a href={`tel:${r.phone}`} className="flex items-center gap-1 hover:text-red-700"><Phone className="w-4 h-4" />{r.phone}</a>
                      <a href={`mailto:${r.email}`} className="flex items-center gap-1 hover:text-red-700"><Mail className="w-4 h-4" />{r.email}</a>
                      {r.city && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{r.city}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-neutral-400 mr-2">{formatDate(r.createdAt)}</span>
                    <Button variant="outline" size="sm" onClick={() => { setSelected(r); setShowDetail(true); }}><Eye className="w-4 h-4" /></Button>
                    <Button variant="outline" size="sm" onClick={() => { setSelected(r); setEditNotes(r.notes || ''); setEditStatus(r.status); setShowEdit(true); }}><Edit2 className="w-4 h-4" /></Button>
                    <Button variant="outline" size="sm" className="text-red-500" onClick={() => deleteRequest(r.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Détail de la demande</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <p><strong>{selected.name}</strong></p>
              <p>{selected.phone} · {selected.email}</p>
              <p>{selected.address} {selected.postalCode} {selected.city}</p>
              <p className="whitespace-pre-wrap bg-neutral-50 p-3 rounded-lg">{selected.description}</p>
              {selected.notes && <p className="bg-yellow-50 p-3 rounded-lg"><strong>Notes:</strong> {selected.notes}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent>
          <DialogHeader><DialogTitle>Modifier</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Statut</label>
              <Select value={editStatus} onValueChange={setEditStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nouveau">Nouveau</SelectItem>
                  <SelectItem value="en_cours">En cours</SelectItem>
                  <SelectItem value="traite">Traité</SelectItem>
                  <SelectItem value="archive">Archivé</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Notes</label>
              <Textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} rows={4} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)}>Annuler</Button>
            <Button onClick={updateRequest} className="bg-red-700 hover:bg-red-600">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminContactRequests;
